import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import api, { mediaUrl } from "../../api/axios";
import { Link, useSearchParams } from "react-router-dom";
import MatchInbox from "./MatchInbox";
import BinderCardViewer from "../Binder/BinderCardViewer";
import { useFeedbackMessage } from "../Common/Feedback/FeedbackHub";

const flowTabs = {
  matches: { label: "Coincidencias", statuses: [] },
  pending: { label: "Pendientes", statuses: ["pending"] },
  coordinated: { label: "Coordinados", statuses: ["accepted", "in_progress"] },
  history: { label: "Historial", statuses: ["completed", "declined", "cancelled", "countered"] }
};

const statusLabels = {
  pending: "Por responder",
  accepted: "Coordinando encuentro",
  in_progress: "Esperando confirmacion",
  completed: "Completado",
  declined: "Rechazado",
  cancelled: "Cancelado",
  countered: "Con respuesta"
};

const reputationTagOptions = [
  { value: "buena_comunicacion", label: "Buena comunicacion" },
  { value: "recomendado", label: "Recomendado" },
  { value: "intercambio_fisico_ok", label: "Interes / intercambio fisico OK" },
  { value: "miembro_confiable", label: "Miembro confiable" }
];

const meetupStatusLabels = {
  not_selected: "Sin lugar definido",
  proposed: "Lugar propuesto",
  confirmed: "Lugar confirmado",
  done: "Encuentro realizado",
  cancelled: "Encuentro cancelado",
  no_show: "Ausencia reportada",
  disputed: "En revision"
};

const tradeCardFront = item => item?.photos?.find(photo => ["front", "binder"].includes(photo.photo_type))?.photo_url || item?.image_url || item?.front_image_url || item?.photo_url || item?.catalog_image_url || "";
const tradeCardBack = item => item?.back_image_url || item?.reverse_image_url || "";

function TradesHome() {
  const [params, setParams] = useSearchParams();
  const [trades, setTrades] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [activeView, setActiveView] = useState(() => params.get("tab") === "matches" ? "matches" : "pending");
  const [activeTrade, setActiveTrade] = useState(null);
  const [counterTrade, setCounterTrade] = useState(null);
  const [meetupTrade, setMeetupTrade] = useState(null);
  const [incidentTrade, setIncidentTrade] = useState(null);
  const [incidentForm, setIncidentForm] = useState({ incidentType: "dispute", details: "" });
  const [cancelTrade, setCancelTrade] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [myCards, setMyCards] = useState([]);
  const [counterForm, setCounterForm] = useState({
    offeredUserCardIds: [],
    message: ""
  });
  const [meetupForms, setMeetupForms] = useState({});
  const [review, setReview] = useState({ rating: 5, comment: "", tags: ["buena_comunicacion", "recomendado"] });
  const [message, setMessage] = useState("");
  const [meetupError, setMeetupError] = useState("");
  const [actionBusy, setActionBusy] = useState("");
  const [viewedTradeCard, setViewedTradeCard] = useState(null);
  const [viewedTradeSide, setViewedTradeSide] = useState("front");

  const loadTrades = () => {
    return api.get("/trades/me").then(({ data }) => setTrades(data)).catch(() => setMessage("No pudimos cargar tus intereses. Intenta actualizar la pagina."));
  };

  useEffect(() => {
    loadTrades();
    api.get("/businesses").then(({ data }) => setBusinesses(data)).catch(() => setBusinesses([]));
    api.get("/binder/me").then(({ data }) => setMyCards(data)).catch(() => setMyCards([]));
  }, []);

  useEffect(() => {
    const focused = trades.find(trade => trade.id === params.get("trade"));
    if (focused) {
      setActiveView(Object.keys(flowTabs).find(key => flowTabs[key].statuses.includes(focused.status)) || "pending");
      requestAnimationFrame(() => document.getElementById("trade-" + focused.id)?.scrollIntoView({ block: "center", behavior: "smooth" }));
    } else if (params.get("tab") === "matches") setActiveView("matches");
  }, [params, trades]);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  useFeedbackMessage(message, message.startsWith("No") ? "danger" : "success");
  const visibleTrades = trades.filter(trade => flowTabs[activeView].statuses.includes(trade.status));
  const tabCount = key => trades.filter(trade => flowTabs[key].statuses.includes(trade.status)).length;

  const updateStatus = async (tradeId, status) => {
    const busyKey = `${tradeId}:${status}`;
    setActionBusy(busyKey); setMessage("");
    try {
      await api.patch(`/trades/${tradeId}/status`, { status });
      setMessage(status === "accepted" ? "Interes aceptado. Ya pueden coordinar el encuentro." : status === "declined" ? "Interes rechazado." : "Intercambio actualizado.");
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo actualizar el intercambio.");
    } finally { setActionBusy(""); }
  };

  const confirmCancellation = async event => {
    event.preventDefault();
    try {
      await api.patch(`/trades/${cancelTrade.id}/status`, {
        status: "cancelled",
        cancellationReason
      });
      setCancelTrade(null);
      setCancellationReason("");
      setMessage("Intercambio cancelado y cartas liberadas.");
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo cancelar el intercambio.");
    }
  };

  const completeTrade = async tradeId => {
    setActionBusy(`${tradeId}:complete`); setMessage("");
    try {
      await api.post(`/trades/${tradeId}/complete`);
      setMessage("Confirmacion enviada. Cardora avisara cuando ambas partes hayan confirmado.");
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo confirmar el intercambio.");
    } finally { setActionBusy(""); }
  };

  const updateMeetupForm = (tradeId, field, value) => {
    setMeetupForms(current => ({
      ...current,
      [tradeId]: {
        ...(current[tradeId] || {}),
        [field]: value
      }
    }));
  };

  const openMeetup = trade => {
    setMeetupError("");
    setMeetupTrade(trade);
    setMeetupForms(current => ({
      ...current,
      [trade.id]: {
        meetupKind: trade.meetup_kind || (trade.meetup_business_id || businesses.length ? "store" : "other"),
        businessId: trade.meetup_business_id || "",
        locationLabel: trade.meetup_location_label || "",
        scheduledAt: trade.meetup_scheduled_at ? trade.meetup_scheduled_at.slice(0, 16) : "",
        meetupNotes: trade.meetup_notes || ""
      }
    }));
  };

  const saveMeetup = async (trade, meetupStatus = "proposed") => {
    const form = meetupForms[trade.id] || {};
    const payload = meetupStatus === "confirmed"
      ? { meetupStatus: "confirmed" }
      : {
        meetupKind: form.meetupKind || "other",
        businessId: form.meetupKind === "store" ? form.businessId || null : null,
        locationLabel: form.meetupKind === "other" ? form.locationLabel || null : null,
        scheduledAt: form.scheduledAt || null,
        meetupNotes: form.meetupNotes || null,
        meetupStatus: "proposed"
      };
    try {
      await api.patch(`/trades/${trade.id}/meetup`, payload);
      setMeetupTrade(null);
      setMeetupError("");
      setMessage(meetupStatus === "confirmed" ? "Encuentro confirmado por ambas partes." : "Propuesta enviada. Falta la confirmacion de la otra persona.");
      await loadTrades();
    } catch (error) {
      setMeetupError(error.response?.data?.error || "No se pudo guardar el encuentro.");
    }
  };

  const openIncident = trade => {
    const canMarkNoShow = trade.meetup_scheduled_at && new Date(trade.meetup_scheduled_at) <= new Date();
    setIncidentTrade(trade);
    setIncidentForm({ incidentType: canMarkNoShow ? "no_show" : "dispute", details: "" });
    setMessage("");
  };

  const submitIncident = async event => {
    event.preventDefault();
    try {
      await api.post(`/trades/${incidentTrade.id}/incidents`, incidentForm);
      setIncidentTrade(null);
      setMessage("Reporte registrado. Cardora conservara la trazabilidad para su revision.");
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo registrar el reporte.");
    }
  };

  const openReview = trade => {
    setActiveTrade(trade);
    setReview({ rating: 5, comment: "", tags: ["buena_comunicacion", "recomendado"] });
    setMessage("");
  };


  const toggleReviewTag = tag => {
    setReview(current => {
      const tags = new Set(current.tags || []);
      if (tags.has(tag)) tags.delete(tag);
      else tags.add(tag);
      return { ...current, tags: Array.from(tags) };
    });
  };

  const sendReview = async event => {
    event.preventDefault();
    try {
      await api.post(`/trades/${activeTrade.id}/reviews`, review);
      setActiveTrade(null);
      setMessage("Valoracion guardada. La otra persona recibio una notificacion.");
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo guardar la valoracion.");
    }
  };

  const shareTrade = async tradeId => {
    setActionBusy(`${tradeId}:share`); setMessage("");
    try {
      await api.post(`/trades/${tradeId}/share`);
      setMessage("Experiencia compartida en el feed.");
      setActiveTrade(null);
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo compartir la experiencia.");
    } finally { setActionBusy(""); }
  };

  const openCounter = trade => {
    setCounterTrade(trade);
    setCounterForm({
      offeredUserCardIds: [],
      message: ""
    });
  };

  const sendCounter = async event => {
    event.preventDefault();
    if (actionBusy) return;
    setActionBusy(`${counterTrade.id}:counter`);
    const requestedUserCardIds = (counterTrade.items || [])
      .filter(item => item.direction === "offered" && item.owner_id !== currentUser?.id)
      .map(item => item.user_card_id);

    try {
      await api.post(`/trades/${counterTrade.id}/counteroffers`, {
        offeredUserCardIds: counterForm.offeredUserCardIds,
        requestedUserCardIds,
        message: counterForm.message
      });
      setCounterTrade(null);
      setMessage("Contraoferta enviada. La otra persona recibira una notificacion.");
      await loadTrades();
    } catch (error) {
      setMessage(error.response?.data?.error || "No se pudo enviar la contraoferta.");
    } finally { setActionBusy(""); }
  };

  const otherUserId = trade => trade.requester_id === currentUser?.id ? trade.receiver_id : trade.requester_id;
  const otherName = trade => trade.requester_id === currentUser?.id ? trade.receiver_name : trade.requester_name;

  const splitItems = trade => ({
    offered: (trade.items || []).filter(item => item.direction === "offered"),
    requested: (trade.items || []).filter(item => item.direction === "requested")
  });

  const viewerConfirmedMeetup = trade => trade.requester_id === currentUser?.id
    ? Boolean(trade.requester_meetup_confirmed_at)
    : Boolean(trade.receiver_meetup_confirmed_at);

  const nextStep = trade => {
    const incoming = trade.receiver_id === currentUser?.id;
    if (trade.status === "pending") {
      return incoming
        ? { title: "Te toca responder", copy: "Acepta el interes, propone otras cartas o rechazalo." }
        : { title: "Esperando respuesta", copy: `${otherName(trade)} recibio tu interes.` };
    }
    if (trade.status === "accepted" && trade.meetup_status === "not_selected") {
      return { title: "Acordad donde encontraros", copy: businesses.length ? "Podeis elegir una tienda Cardora o proponer otro lugar." : "Proponed un lugar publico que resulte comodo a ambos." };
    }
    if (trade.status === "accepted" && trade.meetup_status === "proposed") {
      return viewerConfirmedMeetup(trade)
        ? { title: "Esperando confirmacion del lugar", copy: "La propuesta quedara cerrada cuando la otra persona la confirme." }
        : { title: "Confirma o modifica el encuentro", copy: "Revisa el lugar y horario propuestos antes de aceptarlos." };
    }
    if (trade.status === "accepted") return { title: "Encuentro acordado", copy: "Cuando se realice, ambos deberan confirmarlo." };
    if (trade.status === "in_progress") return { title: "Falta una confirmacion", copy: "El intercambio se cerrara cuando ambas personas confirmen que se realizo." };
    if (trade.status === "completed" && !trade.viewer_reviewed) return { title: "Cuenta como fue", copy: "Tu valoracion construye la confianza de la comunidad." };
    if (trade.status === "completed") return { title: "Intercambio finalizado", copy: "Podreis compartirlo cuando ambos lo hayais valorado." };
    return { title: statusLabels[trade.status] || "Intercambio actualizado", copy: "La actividad queda guardada en tu historial." };
  };

  const meetupTitle = trade => {
    if (trade.meetup_trade_day_name) return `${trade.meetup_trade_day_name} - ${trade.meetup_business_name || "Trade Day"}`;
    if (trade.meetup_business_name) return `${trade.meetup_business_name}${trade.meetup_city ? ` - ${trade.meetup_city}` : ""}`;
    if (trade.meetup_location_label) return trade.meetup_location_label;
    if (trade.meetup_notes) return trade.meetup_notes;
    return "Sin lugar definido";
  };

  return (
    <Container className="page-band trades-page">
      <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
        <div>
          <h1 className="section-title mb-1">Intereses por cartas</h1>
          <p className="muted-copy mb-0">Conexiones iniciadas por cartas, encuentros entre coleccionistas y valoraciones de confianza.</p>
        </div>
      </div>

      <div className="trade-flow-tabs mb-3" role="tablist" aria-label="Estado de los intercambios">
        {Object.entries(flowTabs).map(([key, tab]) => (
          <button type="button" role="tab" aria-selected={activeView === key} className={activeView === key ? "is-active" : ""} onClick={() => { setActiveView(key); setParams(key === "matches" ? { tab: "matches" } : {}, { replace: true }); }} key={key}>
            <span>{tab.label}</span>
            {key !== "matches" && <b>{tabCount(key)}</b>}
          </button>
        ))}
      </div>

      {activeView === "matches" && <MatchInbox myCards={myCards} trades={trades} onCreated={trade => { setMessage("Propuesta enviada. La otra persona recibira un aviso."); setActiveView("pending"); setParams({ trade: trade.id }); void loadTrades(); }} />}
      <div className="d-grid gap-3">
        {activeView !== "matches" && visibleTrades.length === 0 && <Card className="cardora-card"><Card.Body>Aun no tienes intereses por cartas.</Card.Body></Card>}

        {visibleTrades.map(trade => {
          const items = splitItems(trade);
          const guidance = nextStep(trade);
          return (
            <Card id={"trade-" + trade.id} className="cardora-card trade-card" key={trade.id}>
              <Card.Body>
                <div className="trade-card-header">
                  <div>
                    <div className="trade-eyebrow">Intercambio con <Link to={"/u/" + otherUserId(trade)}>{otherName(trade)}</Link></div>
                    <h3>{guidance.title}</h3>
                    <p>{trade.message || "Sin mensaje"}</p>
                  </div>
                  <div className="trade-summary-pills">
                    <span className={`trade-pill is-status is-${trade.status}`}>{trade.status === "pending" ? trade.requester_id === currentUser?.id ? "Enviado · esperando respuesta" : "Recibido · te toca responder" : statusLabels[trade.status] || trade.status}</span>
                    <span className={`trade-pill is-meetup is-${trade.meetup_status || "not_selected"}`}>{meetupStatusLabels[trade.meetup_status] || "Sin lugar definido"}</span>
                  </div>
                </div>

                <TradeFlow status={trade.status} meetupStatus={trade.meetup_status} />
                <p className="trade-next-copy">{guidance.copy}</p>

                <div className="trade-exchange-grid">
                  <div>
                    <span className="trade-column-title">{trade.requester_id === currentUser?.id ? "Cartas que ofreces" : "Cartas que te ofrece"}</span>
                    <div className="trade-items compact">
                      {items.offered.length === 0 && <span className="trade-empty-note">Sin cartas sugeridas</span>}
                      {items.offered.map(item => <TradeItem item={item} onOpen={() => { setViewedTradeCard({ ...item, other_user_id: otherUserId(trade) }); setViewedTradeSide("front"); }} key={item.id} />)}
                    </div>
                  </div>
                  <div>
                    <span className="trade-column-title">{trade.requester_id === currentUser?.id ? "Cartas que te interesan" : "Cartas que le interesan"}</span>
                    <div className="trade-items compact">
                      {items.requested.length === 0 && <span className="trade-empty-note">Abierto a conversacion</span>}
                      {items.requested.map(item => <TradeItem item={item} onOpen={() => { setViewedTradeCard({ ...item, other_user_id: otherUserId(trade) }); setViewedTradeSide("front"); }} key={item.id} />)}
                    </div>
                  </div>
                </div>

                {trade.status === "in_progress" && (
                  <Alert variant="info" className="mt-3 mb-0 trade-inline-alert">
                    Encuentro pendiente de doble confirmacion.
                    <div className="small">
                      {trade.requester_completed_at ? "Solicitante confirmado" : "Falta solicitante"} - {trade.receiver_completed_at ? "Receptor confirmado" : "Falta receptor"}
                    </div>
                  </Alert>
                )}

                {trade.status !== "pending" && trade.status !== "declined" && trade.status !== "cancelled" && (
                  <div className="trade-meetup-summary">
                    <div>
                      <strong>{meetupTitle(trade)}</strong>
                      {trade.meetup_scheduled_at && <span>{new Date(trade.meetup_scheduled_at).toLocaleString("es-ES")}</span>}
                      <small>{trade.requester_meetup_confirmed_at ? "Solicitante OK" : "Falta solicitante"} - {trade.receiver_meetup_confirmed_at ? "Receptor OK" : "Falta receptor"}</small>
                    </div>
                    {['accepted', 'in_progress'].includes(trade.status) && (
                      <Button size="sm" variant="outline-dark" onClick={() => openMeetup(trade)}>{trade.meetup_status === "proposed" ? "Revisar encuentro" : "Acordar encuentro"}</Button>
                    )}
                  </div>
                )}

                <div className="trade-action-row mt-3">
                  <TradeConnectionButton userId={otherUserId(trade)} />
                  {trade.status === "pending" && trade.receiver_id === currentUser?.id && (
                    <>
                      <Button size="sm" className="btn-cardora" disabled={Boolean(actionBusy)} onClick={() => updateStatus(trade.id, "accepted")}>{actionBusy === `${trade.id}:accepted` && <Spinner animation="border" size="sm" className="me-1" />}Quiero coordinarlo</Button>
                      <Button size="sm" variant="outline-dark" onClick={() => openCounter(trade)}>Proponer otras cartas</Button>
                      <Button size="sm" variant="outline-dark" disabled={Boolean(actionBusy)} onClick={() => updateStatus(trade.id, "declined")}>{actionBusy === `${trade.id}:declined` && <Spinner animation="border" size="sm" className="me-1" />}Ahora no</Button>
                    </>
                  )}
                  {trade.status === "pending" && trade.requester_id === currentUser?.id && <span className="trade-waiting-pill">Esperando respuesta</span>}
                  {['accepted', 'in_progress'].includes(trade.status) && trade.meetup_status === "confirmed" && (
                    <Button size="sm" className="btn-cardora" disabled={Boolean(actionBusy)} onClick={() => completeTrade(trade.id)}>
                      {actionBusy === `${trade.id}:complete` && <Spinner animation="border" size="sm" className="me-1" />}{trade.status === "accepted" ? "El intercambio se realizo" : "Confirmar que se realizo"}
                    </Button>
                  )}
                  {trade.status === "completed" && !trade.viewer_reviewed && <Button size="sm" variant="outline-dark" onClick={() => openReview(trade)}>Valorar experiencia</Button>}
                  {trade.status === "completed" && trade.both_reviewed && !trade.feed_shared_at && <Button size="sm" className="btn-cardora" disabled={Boolean(actionBusy)} onClick={() => shareTrade(trade.id)}>{actionBusy === `${trade.id}:share` && <Spinner animation="border" size="sm" className="me-1" />}{actionBusy === `${trade.id}:share` ? "Compartiendo..." : "Compartir experiencia"}</Button>}
                  {trade.status === "completed" && trade.viewer_reviewed && !trade.both_reviewed && <span className="trade-waiting-pill">Esperando la otra valoracion</span>}
                  {trade.feed_shared_at && <span className="trade-waiting-pill">Compartido</span>}
                  {['pending', 'accepted', 'in_progress'].includes(trade.status) && (
                    <Button size="sm" variant="outline-secondary" onClick={() => {
                      setCancelTrade(trade);
                      setCancellationReason("");
                    }}>Cancelar</Button>
                  )}
                  {['accepted', 'in_progress', 'cancelled', 'completed'].includes(trade.status) && !trade.viewer_has_open_incident && (
                    <Button size="sm" variant="link" className="text-danger" onClick={() => openIncident(trade)}>Reportar problema</Button>
                  )}
                  {trade.viewer_has_open_incident && <span className="trade-waiting-pill">Reporte en revision</span>}
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      <Modal show={Boolean(meetupTrade)} onHide={() => setMeetupTrade(null)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Acordar encuentro</Modal.Title></Modal.Header>
        {meetupTrade && (
          <Modal.Body>
            {meetupError && <Alert variant="danger">{meetupError}</Alert>}
            <p className="muted-copy">Puede ser un evento de comunidad, una tienda Cardora, una cafeteria o cualquier punto que acuerden ambas partes.</p>
            <Row className="g-3">
              {meetupTrade.meetup_trade_day_id ? (
                <Col xs={12}>
                  <Alert variant="info" className="mb-0">
                    Este encuentro esta vinculado a <strong>{meetupTrade.meetup_trade_day_name}</strong>.
                  </Alert>
                </Col>
              ) : (
                <Col xs={12}>
                  <Form.Label>Tipo de lugar</Form.Label>
                  <Form.Select value={meetupForms[meetupTrade.id]?.meetupKind || "other"} onChange={event => updateMeetupForm(meetupTrade.id, "meetupKind", event.target.value)}>
                    {businesses.length > 0 && <option value="store">Tienda Cardora</option>}
                    <option value="other">Otro lugar acordado</option>
                  </Form.Select>
                </Col>
              )}
              {!meetupTrade.meetup_trade_day_id && meetupForms[meetupTrade.id]?.meetupKind === "store" && (
              <Col md={6}>
                <Form.Label>Tienda registrada</Form.Label>
                <Form.Select value={meetupForms[meetupTrade.id]?.businessId || ""} onChange={event => updateMeetupForm(meetupTrade.id, "businessId", event.target.value)}>
                  <option value="">Selecciona una tienda</option>
                  {businesses.map(business => <option value={business.id} key={business.id}>{business.name} {business.city ? `- ${business.city}` : ""}</option>)}
                </Form.Select>
              </Col>
              )}
              <Col md={6}>
                <Form.Label>Fecha y hora</Form.Label>
                <Form.Control type="datetime-local" value={meetupForms[meetupTrade.id]?.scheduledAt || ""} onChange={event => updateMeetupForm(meetupTrade.id, "scheduledAt", event.target.value)} />
              </Col>
              <Col xs={12}>
                {!meetupTrade.meetup_trade_day_id && meetupForms[meetupTrade.id]?.meetupKind === "other" && (
                  <Form.Group className="mb-3">
                    <Form.Label>Lugar acordado</Form.Label>
                    <Form.Control value={meetupForms[meetupTrade.id]?.locationLabel || ""} onChange={event => updateMeetupForm(meetupTrade.id, "locationLabel", event.target.value)} placeholder="Ej. Cafeteria de Plaza Mayor" />
                  </Form.Group>
                )}
                <Form.Label>Notas para encontrarse</Form.Label>
                <Form.Control as="textarea" rows={3} value={meetupForms[meetupTrade.id]?.meetupNotes || ""} onChange={event => updateMeetupForm(meetupTrade.id, "meetupNotes", event.target.value)} placeholder="Ej. Mesa del fondo o detalles para reconocerse." />
              </Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-dark" onClick={() => setMeetupTrade(null)}>Cerrar</Button>
          {meetupTrade && meetupTrade.meetup_status === "proposed" && meetupTrade.meetup_proposed_by !== currentUser?.id && (
            <Button className="btn-cardora" onClick={() => saveMeetup(meetupTrade, "confirmed")}>Confirmar lugar y horario</Button>
          )}
          {meetupTrade && !meetupTrade.meetup_trade_day_id && (
            <Button variant={meetupTrade.meetup_proposed_by === currentUser?.id ? "outline-dark" : "dark"} onClick={() => saveMeetup(meetupTrade, "proposed")}>
              {meetupTrade.meetup_status === "proposed" ? "Proponer cambios" : "Proponer encuentro"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(incidentTrade)} onHide={() => setIncidentTrade(null)} centered>
        <Form onSubmit={submitIncident}>
          <Modal.Header closeButton><Modal.Title>Reportar problema</Modal.Title></Modal.Header>
          <Modal.Body>
            {message && <Alert variant="danger">{message}</Alert>}
            <p className="muted-copy">Este reporte queda vinculado al intercambio y puede ser revisado por Cardora.</p>
            <Form.Group className="mb-3">
              <Form.Label>Motivo</Form.Label>
              <Form.Select value={incidentForm.incidentType} onChange={event => setIncidentForm(current => ({ ...current, incidentType: event.target.value }))}>
                {incidentTrade?.meetup_scheduled_at && new Date(incidentTrade.meetup_scheduled_at) <= new Date() && <option value="no_show">No se presento</option>}
                <option value="dispute">Desacuerdo con el intercambio</option>
                <option value="safety">Problema de seguridad</option>
                <option value="other">Otro problema</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Que ocurrio</Form.Label>
              <Form.Control as="textarea" rows={4} maxLength={1500} value={incidentForm.details} onChange={event => setIncidentForm(current => ({ ...current, details: event.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setIncidentTrade(null)}>Cerrar</Button>
            <Button type="submit" variant="danger">Enviar reporte</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(viewedTradeCard)} onHide={() => setViewedTradeCard(null)} centered contentClassName="public-card-preview-modal binder-mobile-card-modal">
        <Modal.Header closeButton><Modal.Title>{viewedTradeCard?.card_name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {viewedTradeCard && <BinderCardViewer
            name={viewedTradeCard.card_name}
            frontImage={mediaUrl(tradeCardFront(viewedTradeCard))}
            backImage={mediaUrl(tradeCardBack(viewedTradeCard))}
            side={viewedTradeSide}
            onRotate={() => setViewedTradeSide(current => current === "front" ? "back" : "front")}
            isZoomed={false}
            zoomOrigin={{ x: 50, y: 50 }}
            onMoveZoom={() => {}}
            onResetZoom={() => {}}
            onToggleZoom={() => {}}
            className="public-card-preview-stage binder-mobile-card-stage"
            imageClassName="public-card-modal-image"
          />}
          {viewedTradeCard && <div className="binder-mobile-card-meta"><strong>{viewedTradeCard.card_name}</strong><span>{[viewedTradeCard.set_name, viewedTradeCard.collector_number, viewedTradeCard.condition].filter(Boolean).join(" - ")}</span></div>}
        </Modal.Body>
        <Modal.Footer>{viewedTradeCard?.other_user_id && <TradeConnectionButton userId={viewedTradeCard.other_user_id} />}<Button variant="light" onClick={() => setViewedTradeCard(null)}>Cerrar</Button></Modal.Footer>
      </Modal>

      <Modal show={Boolean(cancelTrade)} onHide={() => setCancelTrade(null)} centered>
        <Form onSubmit={confirmCancellation}>
          <Modal.Header closeButton><Modal.Title>Cancelar intercambio</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="muted-copy">Las cartas reservadas volveran a estar disponibles. La otra persona recibira una notificacion.</p>
            <Form.Label>Motivo opcional</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              value={cancellationReason}
              onChange={event => setCancellationReason(event.target.value)}
              placeholder="Ej. No podre asistir al encuentro."
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setCancelTrade(null)}>Volver</Button>
            <Button type="submit" variant="danger">Confirmar cancelacion</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(activeTrade)} onHide={() => setActiveTrade(null)} centered>
        <Modal.Header closeButton><Modal.Title>Valorar experiencia</Modal.Title></Modal.Header>
        <Form onSubmit={sendReview}>
          <Modal.Body>
            {message && <Alert variant="success">{message}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Puntuacion</Form.Label>
              <Form.Select value={review.rating} onChange={event => setReview({ ...review, rating: Number(event.target.value) })}>
                <option value={5}>5 - Excelente</option>
                <option value={4}>4 - Muy bueno</option>
                <option value={3}>3 - Correcto</option>
                <option value={2}>2 - Mejorable</option>
                <option value={1}>1 - Mala experiencia</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confianza</Form.Label>
              <div className="review-tag-picker">
                {reputationTagOptions.map(option => (
                  <button
                    type="button"
                    className={(review.tags || []).includes(option.value) ? "is-selected" : ""}
                    onClick={() => toggleReviewTag(option.value)}
                    key={option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Comentario</Form.Label>
              <Form.Control as="textarea" rows={3} value={review.comment} onChange={event => setReview({ ...review, comment: event.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setActiveTrade(null)}>Cerrar</Button>
            <Button type="submit" className="btn-cardora">Guardar</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(counterTrade)} onHide={() => setCounterTrade(null)} centered>
        <Modal.Header closeButton><Modal.Title>Proponer otras cartas</Modal.Title></Modal.Header>
        <Form onSubmit={sendCounter}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Cartas tuyas que podrian interesarle</Form.Label>
              <div className="trade-card-picker-grid trade-counter-picker">
                {myCards.filter(card => card.card_intention === "for_trade" && card.availability === "available").map(card => {
                  const selected = counterForm.offeredUserCardIds.includes(card.id);
                  const image = tradeCardFront(card);
                  return (
                    <button
                      type="button"
                      className={selected ? "is-selected" : ""}
                      disabled={!selected && counterForm.offeredUserCardIds.length >= 3}
                      onClick={() => setCounterForm(current => ({
                        ...current,
                        offeredUserCardIds: selected
                          ? current.offeredUserCardIds.filter(id => id !== card.id)
                          : [...current.offeredUserCardIds, card.id]
                      }))}
                      key={card.id}
                    >
                      {image
                        ? <img src={mediaUrl(image)} alt={card.name} />
                        : <span className="trade-picker-placeholder"><i className="bi bi-card-image" /></span>}
                      <span>
                        <strong>{card.name}</strong>
                        <small>{[card.set_name, card.collector_number, card.condition].filter(Boolean).join(" - ") || "Sin detalles"}</small>
                      </span>
                      <i className={selected ? "bi bi-check-circle-fill" : "bi bi-plus-circle"} />
                    </button>
                  );
                })}
              </div>
              {myCards.filter(card => card.card_intention === "for_trade" && card.availability === "available").length === 0 && (
                <div className="trade-picker-empty">No tienes cartas disponibles para intercambio.</div>
              )}
              <div className="form-hint">{counterForm.offeredUserCardIds.length}/3 seleccionadas. Tambien puedes dejar la propuesta abierta.</div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Mensaje</Form.Label>
              <Form.Control as="textarea" rows={3} value={counterForm.message} onChange={event => setCounterForm({ ...counterForm, message: event.target.value })} placeholder="Me interesa conectar por esta carta. Te dejo una idea inicial..." />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={() => setCounterTrade(null)}>Cerrar</Button>
            <Button type="submit" className="btn-cardora" disabled={Boolean(actionBusy)}>{actionBusy?.endsWith(":counter") && <Spinner animation="border" size="sm" className="me-1" />}{actionBusy?.endsWith(":counter") ? "Enviando..." : "Enviar propuesta"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

function TradeFlow({ status, meetupStatus }) {
  const current = status === "completed" ? 4 : status === "in_progress" ? 3 : status === "accepted" && meetupStatus === "confirmed" ? 3 : status === "accepted" ? 2 : 1;
  return (
    <div className="trade-flow" aria-label={`Paso ${current} de 4`}>
      {["Interes", "Acuerdo", "Encuentro", "Finalizado"].map((label, index) => (
        <div className={index + 1 <= current ? "is-complete" : ""} key={label}>
          <span>{index + 1}</span>
          <small>{label}</small>
        </div>
      ))}
    </div>
  );
}

function TradeItem({ item, onOpen }) {
  const [imageFailed, setImageFailed] = useState(false);
  const image = tradeCardFront(item);

  return (
    <button type="button" className="trade-item" onClick={onOpen}>
      {image && !imageFailed
        ? <img src={mediaUrl(image)} alt={item.card_name} onError={() => setImageFailed(true)} />
        : <span className="trade-item-image-placeholder" aria-label="Carta sin imagen"><i className="bi bi-card-image" /></span>}
      <div><strong>{item.card_name}</strong><span>{tradeCardBack(item) ? "Frente y reverso" : image ? "Ver carta" : "Sin foto disponible"}</span></div>
      <i className="bi bi-chevron-right" />
    </button>
  );
}

function TradeConnectionButton({ userId }) {
  const [connection, setConnection] = useState({ status: "loading" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    api.get(`/social/users/${userId}/status`)
      .then(({ data }) => setConnection(data || { status: "none" }))
      .catch(() => setConnection({ status: "none" }));
  }, [userId]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/users/${userId}/follow`);
      setConnection(data?.status ? { status: data.status, requestId: data.id } : { status: "pending_outgoing" });
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    if (!connection.requestId) return;
    setBusy(true);
    try {
      await api.post(`/social/connection-requests/${connection.requestId}/accept`);
      setConnection({ status: "connected" });
    } finally {
      setBusy(false);
    }
  };

  if (!userId || ["self", "blocked", "blocked_by_me", "loading"].includes(connection.status)) return null;
  if (connection.status === "connected") return <span className="trade-waiting-pill"><i className="bi bi-people-fill me-1" />Conectados</span>;
  if (connection.status === "pending_outgoing") return <span className="trade-waiting-pill">Solicitud enviada</span>;
  if (connection.status === "pending_incoming") return <Button size="sm" variant="outline-dark" disabled={busy} onClick={accept}>{busy && <Spinner animation="border" size="sm" className="me-1" />}Aceptar conexion</Button>;
  return <Button size="sm" variant="outline-dark" disabled={busy} onClick={connect}>{busy && <Spinner animation="border" size="sm" className="me-1" />}<i className="bi bi-person-plus me-1" />Conectar</Button>;
}

export default TradesHome;
